import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, ShieldCheck, Trash2, MonitorSmartphone, KeyRound } from "lucide-react";
import { UserSessionsDialog } from "@/components/UserSessionsDialog";
import { UserPermissionsDialog } from "@/components/UserPermissionsDialog";
import { toast } from "sonner";

export default function Users() {
  const { can, isSuperAdmin, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [permGroups, setPermGroups] = useState([]);

  const [uOpen, setUOpen] = useState(false);
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", role_id: "", branch_ids: [], is_active: true });
  const [uEdit, setUEdit] = useState(null);

  const [rOpen, setROpen] = useState(false);
  const [rForm, setRForm] = useState({ name: "", description: "", permissions: [] });
  const [rEdit, setREdit] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [sessUser, setSessUser] = useState(null);
  const [permUser, setPermUser] = useState(null);

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data));
  useEffect(() => {
    loadUsers(); loadRoles();
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/permissions").then((r) => setPermGroups(r.data.groups));
  }, []);

  const openNewUser = () => { setUForm({ name: "", email: "", password: "", role_id: roles[0]?.id || "", branch_ids: [], is_active: true }); setUEdit(null); setUOpen(true); };
  const openEditUser = (u) => { setUForm({ name: u.name, email: u.email, password: "", role_id: u.role_id, branch_ids: u.branch_ids || [], is_active: u.is_active }); setUEdit(u.id); setUOpen(true); };
  const saveUser = async () => {
    try {
      const payload = { ...uForm, password: (uForm.password || "").trim() || undefined };
      if (uEdit) await api.put(`/users/${uEdit}`, payload); else await api.post("/users", payload);
      toast.success("User saved"); setUOpen(false); loadUsers();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleBranch = (id) => setUForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id] }));

  const confirmDeleteUser = async () => {
    if (!delUser) return;
    try {
      await api.delete(`/users/${delUser.id}`);
      toast.success(`${delUser.name} deleted`);
      setDelUser(null); loadUsers();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openNewRole = () => { setRForm({ name: "", description: "", permissions: [] }); setREdit(null); setROpen(true); };
  const openEditRole = (r) => { setRForm({ name: r.name, description: r.description, permissions: r.permissions.includes("*") ? permGroups.flatMap((g) => g.permissions.map((p) => p.key)) : r.permissions }); setREdit(r.id); setROpen(true); };
  const saveRole = async () => {
    try {
      if (rEdit) await api.put(`/roles/${rEdit}`, rForm); else await api.post("/roles", rForm);
      toast.success("Role saved — users on this role get the new access on their next page load");
      setROpen(false); loadRoles(); refreshUser();
    } catch (e) { toast.error(apiError(e)); }
  };
  const togglePerm = (key) => setRForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((x) => x !== key) : [...f.permissions, key] }));
  const groupAllOn = (g) => g.permissions.every((p) => rForm.permissions.includes(p.key));
  const toggleGroup = (g) => setRForm((f) => {
    const keys = g.permissions.map((p) => p.key);
    const allOn = keys.every((k) => f.permissions.includes(k));
    const set = new Set(f.permissions);
    keys.forEach((k) => (allOn ? set.delete(k) : set.add(k)));
    return { ...f, permissions: [...set] };
  });

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage staff accounts, role-based permissions and branch access" />
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles & Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">{can("users.manage") && <Button data-testid="add-user-btn" onClick={openNewUser}><Plus className="h-4 w-4 mr-1.5" /> Add User</Button>}</div>
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Name</th><th className="py-2.5 px-3">Email</th><th className="py-2.5 px-3">Role</th><th className="py-2.5 px-3">Branches</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium text-foreground">{u.name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{u.email}</td>
                    <td className="py-2 px-3">{u.role_name}{(u.perm_allow?.length > 0 || u.perm_deny?.length > 0) && <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800" title={`${u.perm_allow.length} allowed · ${u.perm_deny.length} denied`} data-testid={`custom-perms-badge-${u.id}`}>Custom</span>}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{u.branch_ids?.length ? u.branch_ids.map((id) => branches.find((b) => b.id === id)?.name).join(", ") : "All"}</td>
                    <td className="py-2 px-3"><StatusBadge value={u.is_active ? "ok" : "out"} label={u.is_active ? "Active" : "Disabled"} /></td>
                    <td className="py-2 px-3 text-right">
                      {isSuperAdmin && <Button variant="ghost" size="sm" title="Custom permissions" data-testid={`perms-user-${u.id}`} onClick={() => setPermUser(u)}><KeyRound className="h-3.5 w-3.5" /></Button>}
                      {can("users.manage") && <Button variant="ghost" size="sm" title="Active sessions" data-testid={`sessions-user-${u.id}`} onClick={() => setSessUser(u)}><MonitorSmartphone className="h-3.5 w-3.5" /></Button>}
                      {can("users.manage") && <Button variant="ghost" size="sm" data-testid={`edit-user-${u.id}`} onClick={() => openEditUser(u)}><Pencil className="h-3.5 w-3.5" /></Button>}
                      {isSuperAdmin && <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" data-testid={`delete-user-${u.id}`} onClick={() => setDelUser(u)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <div className="flex justify-end mb-3">{can("roles.manage") && <Button data-testid="add-role-btn" onClick={openNewRole}><Plus className="h-4 w-4 mr-1.5" /> Add Role</Button>}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((r) => (
              <Card key={r.id} className="p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky-500" /><h3 className="font-display font-semibold text-foreground">{r.name}</h3></div>
                  {can("roles.manage") && <Button variant="ghost" size="sm" data-testid={`edit-role-${r.id}`} onClick={() => openEditRole(r)}><Pencil className="h-3.5 w-3.5" /></Button>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
                <div className="mt-2 text-xs text-muted-foreground">{r.permissions.includes("*") ? "All permissions" : `${r.permissions.length} permissions`}{r.is_system && <span className="ml-2 text-slate-300">· system</span>}</div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* User dialog */}
      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{uEdit ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="user-name-input" value={uForm.name} onChange={(e) => setUForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Email</Label><Input data-testid="user-email-input" value={uForm.email} onChange={(e) => setUForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>Password {uEdit && <span className="text-muted-foreground text-xs">(leave blank to keep)</span>}</Label><Input type="password" autoComplete="new-password" data-testid="user-password-input" value={uForm.password} onChange={(e) => setUForm((f) => ({ ...f, password: e.target.value }))} /></div>
            <div><Label>Role</Label>
              <Select value={uForm.role_id} onValueChange={(v) => setUForm((f) => ({ ...f, role_id: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Branch access <span className="text-muted-foreground text-xs">(none = all branches)</span></Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={uForm.branch_ids.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} data-testid={`user-branch-${b.code}`} /> {b.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={uForm.is_active} onCheckedChange={(v) => setUForm((f) => ({ ...f, is_active: v }))} data-testid="user-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUOpen(false)}>Cancel</Button><Button data-testid="save-user-btn" onClick={saveUser}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role dialog */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{rEdit ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="role-name-input" value={rForm.name} onChange={(e) => setRForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div><Label>Description</Label><Input value={rForm.description} onChange={(e) => setRForm((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Permissions <span className="text-muted-foreground text-xs">({rForm.permissions.length} selected)</span></Label>
              </div>
              {permGroups.map((g) => (
                <div key={g.group}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g.group}</div>
                    <button type="button" className="text-xs font-medium" style={{ color: "var(--mm-action)" }} data-testid={`perm-group-toggle-${g.group}`} onClick={() => toggleGroup(g)}>{groupAllOn(g) ? "Clear" : "Select all"}</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {g.permissions.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={rForm.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} /> {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground px-1">Changes apply immediately. Anyone currently signed in on this role picks up the new access when they reload the app or switch back to the tab.</p>
          <DialogFooter><Button variant="outline" onClick={() => setROpen(false)}>Cancel</Button><Button data-testid="save-role-btn" onClick={saveRole}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete user?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently deletes <span className="font-medium text-foreground">{delUser?.name}</span> ({delUser?.email}).
            Users with any activity or transaction history can't be deleted — disable them instead.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelUser(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-user-btn" onClick={confirmDeleteUser}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UserSessionsDialog target={sessUser} onClose={() => setSessUser(null)} />
      <UserPermissionsDialog target={permUser} role={roles.find((r) => r.id === permUser?.role_id)} groups={permGroups}
        onClose={() => setPermUser(null)} onSaved={() => { loadUsers(); refreshUser(); }} />
    </div>
  );
}
